/**
 * The member's "New Workspace Canvas" policy and the one function that resolves
 * it to a concrete Canvas initializer bundle.
 *
 * This is a SEPARATE state system from App Theme. It only influences a Canvas at
 * new-Workspace creation. See `_docs/architecture/theme-system.md` §11.
 *
 *   preferences.workspace_defaults (persisted, snake_case)
 *     new_workspace_canvas_mode: 'match-app-theme' | 'saved-default'   (default: 'match-app-theme')
 *     saved_canvas_default: { appearance, grid } | null
 *
 *   client model (camelCase)
 *     { newWorkspaceCanvasMode, savedCanvasDefault }
 */

import { getCanvasPreset } from './workspaceCanvasPresets';
import { cloneWorkspaceValue } from './cloneWorkspaceValue';
import { isValidHexColor } from './color';

export const NEW_WORKSPACE_CANVAS_MODES = Object.freeze(['match-app-theme', 'saved-default']);
export const DEFAULT_NEW_WORKSPACE_CANVAS_MODE = 'match-app-theme';

/** Anything not an exact known mode -> `'match-app-theme'`. */
export function normalizeNewWorkspaceCanvasMode(value) {
  return NEW_WORKSPACE_CANVAS_MODES.includes(value)
    ? value
    : DEFAULT_NEW_WORKSPACE_CANVAS_MODE;
}

/** A valid saved-Canvas bundle is `{ appearance: { canvasColor: <hex> }, grid: object }`. */
export function isValidSavedCanvasDefault(bundle) {
  return (
    bundle != null &&
    typeof bundle === 'object' &&
    !Array.isArray(bundle) &&
    bundle.appearance != null &&
    typeof bundle.appearance === 'object' &&
    isValidHexColor(bundle.appearance.canvasColor) &&
    bundle.grid != null &&
    typeof bundle.grid === 'object' &&
    !Array.isArray(bundle.grid)
  );
}

/**
 * Normalize the client-model workspace-defaults object. Unknown / missing /
 * wrong-type mode -> `'match-app-theme'`; a malformed saved default -> `null`.
 */
export function normalizeWorkspaceDefaults(raw) {
  const d = raw != null && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    newWorkspaceCanvasMode: normalizeNewWorkspaceCanvasMode(d.newWorkspaceCanvasMode),
    savedCanvasDefault: isValidSavedCanvasDefault(d.savedCanvasDefault)
      ? cloneWorkspaceValue(d.savedCanvasDefault)
      : null,
  };
}

/**
 * Resolve the Canvas initializer bundle `{ appearance, grid }` for a new Workspace.
 *
 *   mode 'saved-default' + valid saved bundle -> clone(savedCanvasDefault)   (App Theme NOT consulted)
 *   mode 'saved-default' + missing/invalid    -> getCanvasPreset(resolvedTheme)
 *   mode 'match-app-theme' (incl. normalized) -> getCanvasPreset(resolvedTheme)
 *   resolvedTheme unavailable (should not happen) -> getCanvasPreset('dark')  (emergency only)
 *
 * Every branch returns a fresh clone; the Workspace transition re-normalizes.
 */
export function resolveNewWorkspaceCanvasInit({ mode, resolvedTheme, savedCanvasDefault } = {}) {
  const normalizedMode = normalizeNewWorkspaceCanvasMode(mode);
  const theme =
    resolvedTheme === 'light' || resolvedTheme === 'dark' ? resolvedTheme : 'dark';

  if (normalizedMode === 'saved-default' && isValidSavedCanvasDefault(savedCanvasDefault)) {
    return cloneWorkspaceValue(savedCanvasDefault);
  }
  return getCanvasPreset(theme);
}
